/**
 * Legacy magic-wand extraction jobs are polled in two places (single-job
 * status and case-wide query). Both used to append every response, so the
 * Extracted Events menu grew duplicate rows. These helpers keep one row per
 * ``jobId`` and surface in-progress work first.
 */

export type ExtractionJobListItem = {
  jobId?: unknown;
  status?: unknown;
  epochTimeSecs?: unknown;
  epoch_time_secs?: unknown;
  question?: unknown;
};

/** Stable id for list identity. Empty and missing ids are not mergeable. */
export function extractionJobId(job: ExtractionJobListItem | null | undefined): string | null {
  if (job == null || typeof job !== 'object') return null;
  const id = job.jobId;
  if (typeof id !== 'string') return null;
  const trimmed = id.trim();
  return trimmed === '' ? null : trimmed;
}

function isPendingExtractionJob(job: ExtractionJobListItem): boolean {
  return String(job?.status ?? '').toLowerCase() === 'pending';
}

function finiteEpoch(value: unknown): number | undefined {
  // Number(null) and Number('') are 0, which would wipe a stored timestamp.
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const epoch = Number(value);
    return Number.isFinite(epoch) ? epoch : undefined;
  }
  return undefined;
}

/** Prefer ``epochTimeSecs``, then the snake_case field status polls return. */
function epochTimeSecsOf(job: ExtractionJobListItem): number | undefined {
  return finiteEpoch(job.epochTimeSecs) ?? finiteEpoch(job.epoch_time_secs);
}

/**
 * Status polls return a thinner payload than the row we already have. Merge
 * so ``question`` and ``epochTimeSecs`` (what Chat uses to label the job)
 * survive, and normalize ``epoch_time_secs`` onto ``epochTimeSecs``.
 */
function mergeExtractionJob<T extends ExtractionJobListItem>(existing: T, incoming: T): T {
  const epoch = epochTimeSecsOf(incoming) ?? epochTimeSecsOf(existing);
  const question = typeof incoming.question === 'string' ? incoming.question : existing.question;
  return {
    ...existing,
    ...incoming,
    ...(epoch !== undefined ? { epochTimeSecs: epoch } : {}),
    ...(typeof question === 'string' ? { question } : {}),
  };
}

/**
 * Replace the row with the same ``jobId``, or append when this job is new
 * or has no id (id-less rows cannot be told apart, so they are kept).
 */
export function upsertExtractionJob<T extends ExtractionJobListItem>(jobs: readonly T[], job: T): T[] {
  const id = extractionJobId(job);
  if (!id) return [...jobs, job];
  const index = jobs.findIndex((existing) => extractionJobId(existing) === id);
  if (index < 0) return [...jobs, job];
  const next = jobs.slice();
  next[index] = mergeExtractionJob(jobs[index], job);
  return next;
}

/**
 * Keep the last row for each ``jobId``. Rows with no id are all kept.
 * Pending jobs are moved to the front so an in-progress generation is the
 * first entry in the Extracted Events menu.
 */
export function dedupeExtractionJobs<T extends ExtractionJobListItem>(jobs: readonly T[]): T[] {
  const byId = new Map<string, T>();
  const withoutId: T[] = [];
  for (const job of jobs) {
    const id = extractionJobId(job);
    if (!id) {
      withoutId.push(job);
      continue;
    }
    byId.set(id, job);
  }
  const unique = [...byId.values(), ...withoutId];
  return [
    ...unique.filter(isPendingExtractionJob),
    ...unique.filter((job) => !isPendingExtractionJob(job)),
  ];
}
